/**
 * estimateLifecycle — single source of truth for the estimate status lifecycle
 * and the outcome-reason option arrays.
 *
 * Lifecycle:
 *     draft  →  sent  →  won
 *                     \→ lost
 *
 * Stored status values stay in English. Display labels are i18n keys under
 * `status.*` (translated via lib/statusLabels). Outcome-reason option labels
 * are i18n keys under `estimates.outcome_reasons.*` so the dropdown copy can
 * be translated, while the stored `outcome_reason` text remains free-editable
 * (the user can override the dropdown selection with any custom string).
 *
 * Tune the option arrays here when PQ Intelligence finalizes its taxonomy —
 * no other file needs to change.
 */

export const ESTIMATE_STATUSES = ['draft', 'sent', 'won', 'lost'];

export const ACTIVE_STATUSES = ['draft', 'sent'];
export const ARCHIVE_STATUSES = ['won', 'lost'];

export const isActiveStatus = (s) => ACTIVE_STATUSES.includes(normalizeStatus(s));
export const isArchiveStatus = (s) => ARCHIVE_STATUSES.includes(normalizeStatus(s));

/**
 * Maps legacy status values to canonical lifecycle values. Used at hydrate-time
 * so the UI never sees a stale legacy value even before migration 014 has run.
 */
export const normalizeStatus = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'approved') return 'won';
  if (v === 'rejected' || v === 'declined' || v === 'denied') return 'lost';
  if (ESTIMATE_STATUSES.includes(v)) return v;
  return 'draft';
};

/**
 * Outcome-reason options. Each entry has a stable `value` (stored in the DB)
 * and an `i18nKey` (rendered in the dropdown).
 *
 * These are intentionally placeholder values — adjust here when PQ Intelligence
 * locks down its analytics taxonomy.
 */
export const LOST_REASONS = [
  { value: 'price_too_high',     i18nKey: 'estimates.outcome_reasons.lost.price_too_high' },
  { value: 'timing',             i18nKey: 'estimates.outcome_reasons.lost.timing' },
  { value: 'scope_mismatch',     i18nKey: 'estimates.outcome_reasons.lost.scope_mismatch' },
  { value: 'client_unresponsive',i18nKey: 'estimates.outcome_reasons.lost.client_unresponsive' },
  { value: 'job_cancelled',      i18nKey: 'estimates.outcome_reasons.lost.job_cancelled' },
  { value: 'other',              i18nKey: 'estimates.outcome_reasons.lost.other' },
];

export const WON_REASONS = [
  { value: 'price',        i18nKey: 'estimates.outcome_reasons.won.price' },
  { value: 'relationship', i18nKey: 'estimates.outcome_reasons.won.relationship' },
  { value: 'availability', i18nKey: 'estimates.outcome_reasons.won.availability' },
  { value: 'referral',     i18nKey: 'estimates.outcome_reasons.won.referral' },
  { value: 'other',        i18nKey: 'estimates.outcome_reasons.won.other' },
];

/**
 * Returns the reason options for a given status, or [] for statuses that
 * don't capture an outcome reason.
 */
export const reasonsForStatus = (status) => {
  const n = normalizeStatus(status);
  if (n === 'won') return WON_REASONS;
  if (n === 'lost') return LOST_REASONS;
  return [];
};

/**
 * Is the outcome reason required (vs optional) for this status?
 * Lost = required, Won = optional, others = N/A.
 */
export const isReasonRequired = (status) => normalizeStatus(status) === 'lost';

/**
 * Tab-id → status-set map, used by the EstimatesPage tabs.
 */
export const TAB_STATUS_MAP = {
  draft: ['draft'],
  sent:  ['sent'],
  won:   ['won'],
  lost:  ['lost'],
};

export const TAB_IDS = ['draft', 'sent', 'won', 'lost'];
